import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Link,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Tab,
  TabList,
  Tabs,
  Typography,
} from '@mui/joy';
import { BusinessOutlined, MailOutline, TaskAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminAPI, AdminOnboardingAPI } from '../../lib/apiAdmin';
import { OrganizationProfileAPI } from '../../lib/apiOrganizationProfile';
import { getApiErrorMessage } from '../../lib/apiError';
import { StaffPicker } from '../../components/admin/StaffPicker';
import { STALENESS_META, formatCurrency, statusChipColor } from './adminFormat';
import { CustomerDetailDrawer } from './onboarding/CustomerDetailDrawer';
import { AccountPeople } from './accounts/AccountPeople';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { OrganizationTimeline } from './organizations/OrganizationTimeline';
import { OrganizationTasks } from './organizations/OrganizationTasks';
import { ProfileDrafts, type DraftState } from './organizations/ProfileDrafts';
import { SalesHandoff } from './crm/SalesQualification';
import { SETUP_STEP_LABELS } from '../../types/admin';

interface Props {
  initialTab?: string;
  orgId: string | null;
  open: boolean;
  onClose: () => void;
  onImpersonate: () => void;
  onViewTickets: () => void;
}
const date = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 2,
      justifyContent: 'space-between',
      py: 0.75,
      flexWrap: 'wrap',
    }}
  >
    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography level="body-sm" sx={{ overflowWrap: 'anywhere', textAlign: 'right' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);
const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', minWidth: 0 }}>
    <Typography level="title-md" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Sheet>
);

function OrganizationProfile({
  orgId,
  onClose,
  onViewTickets,
  initialTab = 'overview',
  requestAction,
}: Omit<Props, 'open' | 'orgId'> & { orgId: string; requestAction: (action: () => void) => void }) {
  const cache = useQueryClient();
  const [tab, setTab] = useState(initialTab);
  const { startImpersonation } = useImpersonation();
  const [actionError, setActionError] = useState('');
  const [emailTarget, setEmailTarget] = useState('');
  const [emailRequest, setEmailRequest] = useState(0);
  useEffect(() => { setTab(initialTab); setVisited(previous => new Set([...previous, initialTab])); }, [initialTab]);
  const [visited, setVisited] = useState<Set<string>>(() => new Set(['overview', initialTab]));
  const selectTab = (value: string) => {
    setTab(value);
    setVisited((previous) => new Set([...previous, value]));
  };
  const orgQuery = useQuery({
    queryKey: ['admin-org', orgId],
    queryFn: () => AdminAPI.getOrganization(orgId),
  });
  const profile = useQuery({
    queryKey: ['admin', 'org-profile', orgId],
    queryFn: () => OrganizationProfileAPI.profile(orgId),
  });
  const refresh = () => {
    cache.invalidateQueries({ queryKey: ['admin', 'org-profile', orgId] });
    cache.invalidateQueries({ queryKey: ['admin', 'customers'] });
    cache.invalidateQueries({ queryKey: ['admin-orgs'] });
    cache.invalidateQueries({ queryKey: ['admin-org', orgId] });
    cache.invalidateQueries({ queryKey: ['admin', 'customer'] });
  };
  const start = useMutation({
    mutationFn: () => OrganizationProfileAPI.start(orgId),
    onSuccess: refresh,
  });
  const customer = profile.data?.customer;
  const update = useMutation({
    mutationFn: (owner: number | null) =>
      AdminOnboardingAPI.updateCustomer(customer!.id, { cs_owner: owner }),
    onSuccess: refresh,
  });
  const measure = useMutation({
    mutationFn: () => AdminOnboardingAPI.snapshotCustomer(customer!.id),
    onSuccess: refresh,
  });
  const org = orgQuery.data;
  const health = customer?.latest_health;
  return (
    <>
      <ModalClose aria-label="Close organization profile" />
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          pr: 6,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar variant="soft" color="primary" sx={{ width: 48, height: 48 }}>
            <BusinessOutlined />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="body-xs" sx={{ letterSpacing: 1, color: 'primary.500', fontWeight: 700 }}>
              ACCOUNT PROFILE
            </Typography>
            <Typography id="organization-profile-title" level="h3" sx={{ overflowWrap: 'anywhere' }}>
              {org?.name || 'Organization profile'}
            </Typography>
          </Box>
        </Box>
        {org && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              mt: 1.5,
            }}
          >
            <Chip color={statusChipColor(org.subscription_status, org.is_free)}>{org.plan_label}</Chip>
            <Chip variant="outlined" color={STALENESS_META[org.staleness].color}>
              {org.staleness === 'active' ? 'Recently active' : STALENESS_META[org.staleness].label}
            </Chip>
            <Typography level="body-sm">Customer since {date(org.created_at)}</Typography>
          </Box>
        )}
      </Box>
      {orgQuery.isPending && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress aria-label="Loading organization" />
        </Box>
      )}
      {orgQuery.isError && (
        <Alert
          color="danger"
          sx={{ m: 2 }}
          endDecorator={<Button onClick={() => orgQuery.refetch()}>Retry</Button>}
        >
          Could not load this organization.
        </Alert>
      )}
      {org && (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => v && selectTab(String(v))}
            sx={{ px: { xs: 1, md: 3 }, py: 1, bgcolor: 'transparent' }}
          >
            <TabList aria-label="Organization profile sections" sx={{ flexWrap: 'wrap' }}>
              {[
                ['overview', 'Overview'],
                ['people', 'People'],
                ['timeline', 'Timeline & messages'],
                ['onboarding', 'Onboarding'],
                ['tasks', 'Tasks'],
                ['account', 'Billing & details'],
              ].map(([v, label]) => (
                <Tab key={v} value={v} sx={{ flexShrink: 0 }}>
                  {label}
                </Tab>
              ))}
            </TabList>
          </Tabs>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              pt: 1,
              overflowY: 'auto',
              minHeight: 0,
              flex: 1,
            }}
          >
            {profile.isError && (
              <Alert
                color="danger"
                sx={{ mb: 2 }}
                endDecorator={<Button onClick={() => profile.refetch()}>Retry</Button>}
              >
                Onboarding details could not be loaded.
              </Alert>
            )}
            {actionError && <Alert color="danger">{actionError}</Alert>}
            {(update.error || measure.error || start.error) && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {getApiErrorMessage(
                  update.error || measure.error || start.error,
                  'The update could not be saved.',
                )}
              </Alert>
            )}
            <Box hidden={tab !== 'overview'}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2,minmax(0,1fr))',
                    md: 'repeat(4,minmax(0,1fr))',
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {[
                  ['Monthly revenue', `${formatCurrency(org.mrr)}/mo`],
                  ['Active users', org.user_count],
                  ['Open tickets', org.open_ticket_count],
                  ['Open CX tasks', customer?.open_task_count ?? '—'],
                ].map(([label, value]) => (
                  <Sheet key={label} variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                    <Typography level="body-xs">{label}</Typography>
                    <Typography level="h3" sx={{ mt: 0.5 }}>
                      {value}
                    </Typography>
                  </Sheet>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0,1fr)',
                    md: 'minmax(0,1fr) minmax(0,1fr)',
                  },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Card title="Next step">
                    <Typography level="title-md">{org.cx?.next_task || org.cx?.next_step || 'Review account progress'}</Typography>
                    <Typography level="body-sm">{org.cx?.owner_name ? `Owned by ${org.cx.owner_name}` : 'Assign a Customer Success owner below.'}{org.cx?.next_due ? ` Due ${date(org.cx.next_due)}.` : ''}</Typography>
                    {!!org.cx?.overdue_tasks && <Chip color="danger" sx={{ mt: 1 }}>{org.cx.overdue_tasks} overdue tasks</Chip>}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}><Button variant="soft" onClick={() => selectTab('tasks')}>View tasks</Button><Button variant="plain" onClick={() => selectTab('onboarding')}>Review onboarding · {org.cx?.completed_steps ?? 0}/{org.cx?.total_steps ?? 0}</Button></Box>
                  </Card>
                  <Card title="Primary contact">
                    <Typography level="title-lg">{org.owner_name || 'Contact name not added'}</Typography>
                    <Box sx={{ display: 'grid', gap: 0.75, mt: 1 }}>
                      {org.owner_email && (
                        <Link href={`mailto:${org.owner_email}`} sx={{ overflowWrap: 'anywhere' }}>
                          {org.owner_email}
                        </Link>
                      )}
                      {org.owner_phone && <Link href={`tel:${org.owner_phone}`}>{org.owner_phone}</Link>}
                      <Typography level="body-sm">
                        {[org.main_office_city, org.main_office_state, org.main_office_zip]
                          .filter(Boolean)
                          .join(', ') || 'Location not added'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                      <Button startDecorator={<MailOutline />} onClick={() => requestAction(() => { setEmailRequest(n => n + 1); setEmailTarget(org.owner_email || org.company_email || ''); selectTab('timeline'); })}>
                        Email account
                      </Button>
                      <Button
                        variant="outlined"
                        startDecorator={<TaskAlt />}
                        onClick={() => selectTab('tasks')}
                      >
                        Add follow-up
                      </Button>
                    </Box>
                  </Card>
                  <Card title="Customer Success">
                    <FormControl>
                      <FormLabel>Account owner</FormLabel>
                      <StaffPicker
                        value={customer?.cs_owner ?? null}
                        onChange={(owner) => update.mutate(owner)}
                        disabled={!customer || update.isPending}
                      />
                    </FormControl>
                    {profile.isPending && <Typography role="status">Loading onboarding…</Typography>}
                    {!profile.isPending && !profile.isError && !customer && (
                      <Button sx={{ mt: 1 }} loading={start.isPending} onClick={() => start.mutate()}>
                        Start onboarding
                      </Button>
                    )}
                    {customer?.notes && (
                      <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', mt: 1.5 }}>
                        {customer.notes}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        mt: 1.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button variant="soft" onClick={() => selectTab('onboarding')}>
                        Open onboarding plan
                      </Button>
                      <Button variant="plain" onClick={onViewTickets}>
                        View support tickets
                      </Button>
                    </Box>
                  </Card>
                </Box>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Card title="Account health">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography level="h3">{health ? `${health.score}/100` : 'Not measured'}</Typography>
                      {customer && (
                        <Button
                          size="sm"
                          variant="outlined"
                          loading={measure.isPending}
                          onClick={() => measure.mutate()}
                        >
                          Measure health
                        </Button>
                      )}
                    </Box>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      {health
                        ? `${health.band.replace(/_/g, ' ')} · measured ${date(health.taken_on)}`
                        : 'Measure health to see setup progress and actual product usage.'}
                    </Typography>
                    {health && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.75,
                          flexWrap: 'wrap',
                          mt: 1.5,
                        }}
                      >
                        {(Array.isArray(health.counts.setup_missing) ? health.counts.setup_missing : []).map(
                          (key) => (
                            <Chip key={key} size="sm" variant="outlined">
                              {SETUP_STEP_LABELS[key] || key}
                            </Chip>
                          ),
                        )}
                      </Box>
                    )}
                    <Row label="Last active" value={date(org.last_activity_at)} />
                    <Row label="Contacts created" value={org.contact_count} />
                    <Row label="Jobs created" value={org.job_count} />
                  </Card>
                  <Card title="Connected records">
                    {profile.data?.leads.map((lead) => (
                      <Link
                        key={lead.id}
                        href={`/manage/crm?contact=${lead.id}`}
                        sx={{ display: 'block', mb: 1 }}
                      >
                        {lead.name} · CRM contact
                      </Link>
                    ))}
                    {!profile.data?.leads.length && (
                      <Typography level="body-sm">No linked CRM contacts.</Typography>
                    )}
                    <Button variant="plain" onClick={() => selectTab('people')} sx={{ mt: 1 }}>
                      View all users
                    </Button>
                  </Card>
                </Box>
              </Box>
            </Box>
            {tab === 'overview' && <SalesHandoff answers={customer?.sales_qualification} />}
            {visited.has('people') && <Box hidden={tab !== 'people'}><AccountPeople orgId={orgId} onEmail={email => requestAction(() => {setEmailRequest(n => n + 1); setEmailTarget(email); selectTab('timeline');})} onImpersonate={user => requestAction(() => {startImpersonation(user).catch(error => setActionError(getApiErrorMessage(error, 'Could not sign in as this user.')));})} /></Box>}
            {visited.has('timeline') && (
              <Box hidden={tab !== 'timeline'}>
                <OrganizationTimeline key={emailRequest} org={org} doNotEmail={!!customer?.do_not_email} initialEmail={emailTarget} />
              </Box>
            )}
            {visited.has('onboarding') && (
              <Box hidden={tab !== 'onboarding'}>
                {customer ? (
                  <CustomerDetailDrawer customerId={customer.id} onClose={() => {}} embedded />
                ) : (
                  <Alert color="neutral">
                    {profile.isPending
                      ? 'Loading customer record…'
                      : 'Choose Start onboarding in Overview to set up this account’s plan.'}
                  </Alert>
                )}
              </Box>
            )}
            {visited.has('tasks') && (
              <Box hidden={tab !== 'tasks'}>
                <OrganizationTasks orgId={org.id} />
              </Box>
            )}
            <Box
              hidden={tab !== 'account'}
              sx={{
                display: tab === 'account' ? 'grid' : 'none',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Card title="Subscription">
                <Row label="Plan" value={org.plan_label} />
                <Row label="Billing cycle" value={org.subscription?.billing_cycle} />
                <Row label="Seats" value={org.seat_count} />
                <Row
                  label="Price per seat"
                  value={org.subscription ? formatCurrency(org.subscription.effective_price_per_seat) : '—'}
                />
                <Row label="Trial deadline" value={date(org.subscription?.trial_end)} />
                <Row
                  label={org.subscription?.cancellation_effective_at ? 'Plan ends' : 'Current period ends'}
                  value={date(
                    org.subscription?.cancellation_effective_at || org.subscription?.current_period_end,
                  )}
                />
                {org.subscription?.pause_collection?.behavior && (
                  <Alert color="warning">Payment collection is paused in Stripe.</Alert>
                )}
                <Row label="Stripe customer" value={org.subscription?.stripe_customer_id} />
              </Card>
              <Card title="Organization details">
                <Row label="Company email" value={org.company_email} />
                <Row label="Company phone" value={org.company_phone_number} />
                <Row label="Website" value={org.website} />
                <Row label="Organization ID" value={org.id} />
                <Button
                  component="a"
                  href={`/admin/core/organization/${org.id}/change/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  Edit organization in Django admin
                </Button>
              </Card>
              <Card title="Recent account activity">
                {org.recent_activity.length ? (
                  org.recent_activity.map((event, index) => (
                    <Box
                      key={index}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography level="body-sm">
                        {event.description || event.event_type.replace(/_/g, ' ')}
                      </Typography>
                      <Typography level="body-xs">
                        {date(event.timestamp)}
                        {event.user ? ` · ${event.user}` : ''}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography level="body-sm">No recent account audit events.</Typography>
                )}
              </Card>
            </Box>
          </Box>
        </>
      )}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography level="body-xs">ApexOS · Customer Success</Typography>
        <Button variant="plain" color="neutral" onClick={onClose}>
          Close profile
        </Button>
      </Box>
    </>
  );
}

/** Existing entry points now open a spacious, tabbed profile. */
export function OrgDetailDrawer({ orgId, open, ...props }: Props) {
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [leave, setLeave] = useState<(() => void) | null>(null);
  const report = useCallback(
    (key: string, state: DraftState) => setDrafts((previous) => ({ ...previous, [key]: state })),
    [],
  );
  const dirty = Object.values(drafts).some((draft) => draft.dirty);
  const busy = Object.values(drafts).some((draft) => draft.busy);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const requestLeave = (action: () => void) => {
    if (busy) return;
    if (dirty) setLeave(() => action);
    else action();
  };
  return (
    <>
      <Modal open={open} onClose={() => requestLeave(props.onClose)}>
        <ModalDialog
          aria-labelledby="organization-profile-title"
          sx={{
            width: { xs: 'calc(100vw - 16px)', md: '85vw' },
            height: { xs: 'calc(100dvh - 16px)', md: '85dvh' },
            maxWidth: 'none',
            p: 0,
            gap: 0,
            overflow: 'hidden',
            borderRadius: { xs: 'md', md: 'xl' },
          }}
        >
          <ProfileDrafts.Provider value={report}>
            {orgId && (
              <OrganizationProfile
                key={orgId}
                orgId={orgId}
                initialTab={props.initialTab}
                requestAction={requestLeave}
                onClose={() => requestLeave(props.onClose)}
                onViewTickets={() => requestLeave(props.onViewTickets)}
                onImpersonate={() => requestLeave(props.onImpersonate)}
              />
            )}
          </ProfileDrafts.Provider>
        </ModalDialog>
      </Modal>
      <Modal open={!!leave} onClose={() => setLeave(null)}>
        <ModalDialog role="alertdialog" aria-labelledby="discard-profile-drafts">
          <Typography id="discard-profile-drafts" level="title-lg">
            Discard unsaved drafts?
          </Typography>
          <Typography>Your note, message, or new task has not been saved.</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setLeave(null)}>
              Keep editing
            </Button>
            <Button
              color="danger"
              onClick={() => {
                const action = leave;
                setLeave(null);
                action?.();
              }}
            >
              Discard drafts
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
}
export default OrgDetailDrawer;
